/**
 * Pickup points — génération de points de rendez-vous covoit.
 *
 * Sprint 6 : on ne demande JAMAIS au passager de partager son adresse
 * exacte. À la place, on suggère 1-3 "pickup points" — des POI publics
 * (arrêt de bus, école, parc, supermarché) qui sont :
 *   1. Proches du domicile du passager (≤ 500m)
 *   2. À une distance raisonnable du domicile (≥ 200m, privacy)
 *   3. Sur ou très proches de l'itinéraire du conducteur (≤ 250m)
 *
 * Pourquoi 200m de distance minimale ?
 * → Anti-déduction : si on suggère un point à 50m du domicile, un attaquant
 * qui voit la liste des pickups peut trianguler l'adresse. À 200m+ et avec
 * des POI publics, on a une protection raisonnable contre cette attaque.
 *
 * Source des POI : Mapbox Tilequery API sur les layers
 *   - poi_label : commerces, écoles, parcs, restaurants
 *   - transit_stop_label : arrêts de bus, stations de métro/tram
 *
 * @see src/lib/mapbox/matching.ts (distance polyline)
 * @see Karos blog "trust by design" — modèle de référence covoit privacy-first
 */
import { fetchTilequery, haversineDistanceM, type LngLat } from './client';
import {
  distancePointToPolylineM,
  type LngLatTuple,
} from './matching';

export interface PickupPoint {
  /** Coords du POI. */
  lng: number;
  lat: number;
  /** Nom affiché (ex : "Arrêt République", "École Jean-Moulin"). */
  name: string;
  /** Catégorie POI (poi_label, transit_stop_label, etc.). */
  category: string;
  /** Distance domicile passager → POI, en mètres. */
  distanceFromHomeM: number;
  /** Distance POI → route conducteur, en mètres. */
  distanceToRouteM: number;
  /** Score de qualité 0..1 (combine proximité home + alignement route). */
  score: number;
}

export interface PickupPointsOptions {
  /** Rayon de recherche autour du domicile (Tilequery max 1000m). */
  searchRadiusM?: number;
  /** Distance min POI ↔ domicile (privacy). Défaut 200m. */
  minDistanceFromHomeM?: number;
  /** Distance max POI ↔ domicile (utilité). Défaut 500m. */
  maxDistanceFromHomeM?: number;
  /** Distance max POI ↔ route conducteur. Défaut 250m. */
  maxDistanceToRouteM?: number;
  /** Nombre max de pickup points retournés. Défaut 3. */
  maxResults?: number;
}

/**
 * Génère 1-N pickup points pour un passager donné, sur la route d'un
 * conducteur donné.
 *
 * Algorithme :
 *  1. Tilequery autour du domicile passager (rayon 600m par défaut)
 *  2. Filtre privacy : distance home ≥ 200m
 *  3. Filtre utilité : distance home ≤ 500m
 *  4. Filtre alignement : distance route ≤ 250m
 *  5. Score : pondération distance home (×0.4) + distance route (×0.6)
 *  6. Retourne top N triés par score décroissant
 */
export async function generatePickupPoints(
  passengerHome: LngLat,
  driverRoute: LngLatTuple[],
  options: PickupPointsOptions = {},
): Promise<PickupPoint[]> {
  const {
    searchRadiusM = 600,
    minDistanceFromHomeM = 200,
    maxDistanceFromHomeM = 500,
    maxDistanceToRouteM = 250,
    maxResults = 3,
  } = options;

  // Tilequery a une limite max de 1000m côté Mapbox API
  const radius = Math.min(searchRadiusM, 1000);
  const features = await fetchTilequery(passengerHome, radius);
  if (features.length === 0) return [];

  const candidates: PickupPoint[] = [];

  for (const feat of features) {
    const [lng, lat] = feat.geometry.coordinates;
    const distFromHome = haversineDistanceM(
      passengerHome,
      { lng, lat },
    );

    // Filtre privacy + utilité
    if (distFromHome < minDistanceFromHomeM) continue;
    if (distFromHome > maxDistanceFromHomeM) continue;

    const { distanceM: distToRoute } = distancePointToPolylineM(
      { lng, lat },
      driverRoute,
    );

    // Filtre alignement route
    if (distToRoute > maxDistanceToRouteM) continue;

    // Score : combinaison normalisée — plus c'est proche route ET pas trop
    // près du domicile, mieux c'est. On normalise sur les bornes du filtre.
    const homeNorm = 1 - (distFromHome - minDistanceFromHomeM) /
      (maxDistanceFromHomeM - minDistanceFromHomeM);
    const routeNorm = 1 - distToRoute / maxDistanceToRouteM;
    const score = 0.4 * homeNorm + 0.6 * routeNorm;

    const props = feat.properties ?? {};
    const name = (props.name_fr as string) || (props.name as string) || 'Point de rendez-vous';
    const category = (props.class as string) || (props.type as string) || 'poi';

    candidates.push({
      lng,
      lat,
      name,
      category,
      distanceFromHomeM: Math.round(distFromHome),
      distanceToRouteM: Math.round(distToRoute),
      score,
    });
  }

  candidates.sort((a, b) => b.score - a.score);
  return candidates.slice(0, maxResults);
}
